import {
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  FontWeight,
  Markdown,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useContext, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { useVolatileSettings } from '../../../../hooks';
import { UseVolatileSettings } from '../../../../interfaces';
import { PageDataContext } from '../../components/page';
import { ScrollElearningContentSlideType } from '../../enums';
import { PageData } from '../../interfaces';
import { ChoiceQuiz } from '../choiceQuiz/choiceQuiz.component';
import { DragAndDrop } from '../dragAndDrop/dragAndDrop.component';
import { MatchToCategory } from '../matchToCategory/matchToCategory.component';
import { ProgressiveQuiz } from '../progressiveQuiz/progressiveQuiz.component';

import QuestionMarks from './images/questionMarks.png';
import {
  ContentContainer,
  ContentWrapper,
  Description,
  HeaderWrapper,
  Image,
  ImageWrapper,
  StyledButton,
  StyledHeaderTypography,
  Wrapper,
} from './interactionSlide.styled';
import { InteractionSlideProps } from './interactionSlide.types';

export const InteractionSlide: React.FC<InteractionSlideProps> = (props: InteractionSlideProps): JSX.Element => {
  const { baseUrl, callComplete, completed }: InteractionSlideProps = props;
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const {
    slidesOverallCounter,
    currentSlideCounter,
    title,
    description,
    elearningContent,
  }: Partial<PageData> = dataCtx ?? {};
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const { t }: TransProps<never> = useTranslation();
  const [isInteractionActive, setIsInteractionActive]: UseState<boolean> = useState(false);
  const { changeComparisonTablePopupVisible }: UseVolatileSettings = useVolatileSettings();

  const getInteraction: () => JSX.Element | undefined = (): JSX.Element | undefined => {
    switch (elearningContent?.type) {
      case ScrollElearningContentSlideType.ChoiceQuiz:
        return (
          <ChoiceQuiz
            baseUrl={baseUrl}
            callComplete={callComplete}
            header={elearningContent.header}
            layoutSettings={elearningContent.layoutSettings}
            questions={elearningContent.quizQuestions}
            quizImages={elearningContent.quizImages}
            subheader={elearningContent.subheader}
            type={elearningContent.quizType}
          />
        );
      case ScrollElearningContentSlideType.ProgressiveQuiz:
        return (
          <ProgressiveQuiz
            baseUrl={baseUrl}
            callComplete={callComplete}
            data={elearningContent.progressiveQuizData}
          />
        );
      case ScrollElearningContentSlideType.MatchToCategory:
        return (
          <MatchToCategory
            baseUrl={baseUrl}
            callComplete={callComplete}
            content={elearningContent.matchToCategoryData}
            failImage={elearningContent.matchToCategoryData.failImage}
          />
        );
      case ScrollElearningContentSlideType.DragAndDrop:
        return (
          <DragAndDrop callComplete={callComplete} content={elearningContent.dragAndDropData} />
        );
      default:
        return;
    }
  };

  const onButtonClick: () => void = (): void => {
    if (elearningContent?.type === ScrollElearningContentSlideType.InteractionSlideCustomAction) {
      switch (elearningContent.customAction) {
        case 'showComparisonTable':
          changeComparisonTablePopupVisible(true);
          if (!completed) {
            callComplete();
          }
          break;
      }
    } else {
      setIsInteractionActive(true);
    }
  };

  return (
    <Wrapper>
      <HeaderWrapper>
        <StyledHeaderTypography variant={TypographyVariant.TextSM}>
          <Markdown
            text={t('competence.app.scrollLesson.componentsV2.interactionSlide.slides', { currentSlideCounter, slidesOverallCounter })}
          />
        </StyledHeaderTypography>
        <StyledHeaderTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextXSAllCaps}>
          {t('competence.app.scrollLesson.componentsV2.interactionSlide.interaction')}
        </StyledHeaderTypography>
      </HeaderWrapper>
      {isInteractionActive ? (
        getInteraction()
      ) : (
        <ContentWrapper>
          <ContentContainer>
            {!!title && (
              <Typography
                fontWeight={FontWeight.Bold}
                tag={TypographyElement.div}
                variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}
              >
                <Markdown text={title} />
              </Typography>
            )}
            {!!description && (
              <Typography variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}>
                <Description text={description} />
              </Typography>
            )}
            {!isSmallDesktop && (
              <ImageWrapper>
                <Image src={QuestionMarks} />
              </ImageWrapper>
            )}
            <StyledButton
              fullWidth={!isSmallDesktop}
              label={elearningContent?.type === ScrollElearningContentSlideType.InteractionSlideCustomAction && elearningContent.buttonLabel
                ? elearningContent.buttonLabel
                : t('competence.app.scrollLesson.componentsV2.interactionSlide.start')
              }
              onClick={onButtonClick}
              size={ButtonSize.Large}
              variant={ButtonTheme.PrimaryBrand}
            />
          </ContentContainer>
          {!!isSmallDesktop && (
            <ImageWrapper>
              <Image src={QuestionMarks} />
            </ImageWrapper>
          )}
        </ContentWrapper>
      )}
    </Wrapper>
  );
};
